import React, { useCallback, useEffect, useState } from 'react';
import { CloudDownload, Loader2, CheckCircle, AlertCircle, RefreshCw, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const Stat = ({ label, value, testId, tone = 'text-[#1A1F2E]' }) => (
  <div className="rounded-md border border-[#E5DFD0] bg-[#FFFBF5] px-3 py-2" data-testid={testId}>
    <div className={`text-lg font-bold ${tone}`}>{value ?? 0}</div>
    <div className="text-xs text-[#5A5F6E]">{label}</div>
  </div>
);

const RunSummary = ({ run }) => {
  if (!run.ok) {
    return (
      <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="erp-fetch-error">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {run.error || 'Fetch failed'}
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="erp-fetch-result">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="New slips imported" value={run.imported} testId="erp-fetch-imported-count" tone="text-green-700" />
        <Stat label="Already in WMS" value={run.skipped_existing} testId="erp-fetch-existing-count" />
        <Stat label="Other stock points" value={run.skipped_stock_point} testId="erp-fetch-other-sp-count" />
        <Stat label="Nothing pending" value={run.skipped_no_pending} testId="erp-fetch-nopending-count" />
      </div>
      {run.imported_slips?.length > 0 && (
        <p className="text-xs text-[#5A5F6E] break-words" data-testid="erp-fetch-imported-list">
          Imported: {run.imported_slips.join(', ')}
        </p>
      )}
    </div>
  );
};

export const ErpPickSlipFetchCard = () => {
  const [runs, setRuns] = useState([]);
  const [fetching, setFetching] = useState(false);

  const loadLog = useCallback(async () => {
    try {
      const r = await apiClient.get('/wms/fg/erp/pick-slips/fetch-log?limit=5');
      setRuns(r.data.runs || []);
    } catch { /* log is informational only */ }
  }, []);

  useEffect(() => { loadLog(); }, [loadLog]);

  const fetchNow = async () => {
    setFetching(true);
    try {
      const r = await apiClient.post('/wms/fg/erp/pick-slips/fetch');
      if (r.data.ok) toast.success(`${r.data.imported} new pick slip(s) imported from ERP`);
      else toast.error(r.data.error || 'ERP fetch failed');
      loadLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'ERP fetch failed');
    } finally {
      setFetching(false);
    }
  };

  const last = runs[0];
  return (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 md:p-8 mb-6" data-testid="erp-fetch-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-[#1A1F2E] font-display flex items-center gap-2">
            <CloudDownload className="w-5 h-5 text-[#2D5F4F]" /> Fetch from Halnode ERP
          </h2>
          <p className="text-sm text-[#5A5F6E] mt-1 flex items-center gap-1" data-testid="erp-auto-sync-note">
            <RefreshCw className="w-3.5 h-3.5" /> Auto-syncs every 15 minutes · pending quantity only · existing slips skipped
          </p>
        </div>
        <Button onClick={fetchNow} disabled={fetching} className="bg-[#2D5F4F] hover:bg-[#244d40]" data-testid="erp-fetch-now-btn">
          {fetching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-2" />}
          {fetching ? 'Fetching…' : 'Fetch from ERP now'}
        </Button>
      </div>
      {last ? (
        <div className="space-y-3">
          <p className="text-xs text-[#5A5F6E] flex items-center gap-1" data-testid="erp-last-sync">
            {last.ok ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Clock className="w-3.5 h-3.5" />}
            Last sync {fmtTime(last.finished_at)} · {last.trigger === 'cron' ? 'automatic' : `manual by ${last.run_by}`} · {last.fetched ?? 0} slip(s) received
          </p>
          <RunSummary run={last} />
        </div>
      ) : (
        <p className="text-sm text-[#5A5F6E]" data-testid="erp-no-sync-yet">No ERP sync has run yet.</p>
      )}
    </div>
  );
};

export default ErpPickSlipFetchCard;
